/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

/**
 * usePushNotifications – React hook for push notification management
 *
 * Provides:
 *   - isSupported: browser can do push
 *   - isSubscribed: user is currently subscribed
 *   - permission: 'granted' | 'denied' | 'default'
 *   - subscribe(): subscribe to push
 *   - unsubscribe(): unsubscribe from push
 *   - loading: operation in progress
 *   - error: last error message
 *
 * Usage:
 *   const { isSubscribed, subscribe, unsubscribe, permission } = usePushNotifications();
 */

import { useState, useEffect, useCallback } from 'react';
import {
  isPushSupported,
  isSubscribedToPush,
  subscribeToPush,
  unsubscribeFromPush,
} from '../lib/pushNotifications';

export function usePushNotifications() {
  const [isSupported] = useState(() => isPushSupported());
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [permission, setPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'default'
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Check subscription status on mount
  useEffect(() => {
    if (!isSupported) return;
    isSubscribedToPush().then(setIsSubscribed);
  }, [isSupported]);

  const subscribe = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const sub = await subscribeToPush();
      setIsSubscribed(true);
      setPermission(Notification.permission);
      return sub;
    } catch (err) {
      setPermission(typeof Notification !== 'undefined' ? Notification.permission : 'default');
      if (typeof Notification !== 'undefined' && Notification.permission === 'denied') {
        setError('Notification permission was denied. Please enable it in your browser settings.');
      } else {
        setError(err.message || 'Failed to enable push notifications');
      }
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const unsubscribe = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const ok = await unsubscribeFromPush();
      if (ok) setIsSubscribed(false);
      return ok;
    } catch (err) {
      setError(err.message);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    isSupported,
    isSubscribed,
    permission,
    loading,
    error,
    subscribe,
    unsubscribe,
  };
}

export default usePushNotifications;
